const idOf = (value) => {
  const text = String(value ?? "");
  return /^\d+$/.test(text) ? text.replace(/^0+/, "") : "";
};
const active = (row, fields) => fields.every((field) => row[field] == null || String(row[field]) === "0");
const activeAsset = (row) => active(row, ["process_status", "register_asset_status"]);
const machineLabel = (row) => row.safe_machine_name || row.machine_name || "설비·구역";
const displayNames = (row) => ({
  title: row.safe_machine_name || row.machine_name || "설비·구역",
  detail: row.process_name || "",
});
const unique = (items) => [...new Map(items.filter((item) => item.id).map((item) => [item.id, item])).values()];

// Carry a field-specific description only when it differs from the common wording.
export function buildCopySnapshot(kind, { machines = [], assets = [], causes = [] }) {
  if (kind === "machine") {
    return unique(machines.map((machine) => ({
      id: idOf(machine.machine_id),
      label: machineLabel(machine),
      ...displayNames(machine),
      items: unique(assets.filter((asset) => activeAsset(asset) && idOf(asset.machine_id) === idOf(machine.machine_id))
        .map((asset) => ({ id: idOf(asset.process_id), label: asset.process_name || "평가 대상" }))),
    })));
  }
  if (kind !== "cause") throw new Error("지원하지 않는 복사 유형입니다.");
  return unique(assets.filter(activeAsset).map((asset) => ({
    id: idOf(asset.register_asset_id),
    label: `${machineLabel(asset)} · ${asset.process_name || "평가 대상"}`,
    ...displayNames(asset),
    items: unique(causes.filter((cause) => activeAsset(cause) && active(cause, ["register_cause_status"])
      && idOf(cause.register_asset_id) === idOf(asset.register_asset_id))
      .map((cause) => ({
        id: idOf(cause.danger_cause_id),
        ...(cause.common_danger_description != null && cause.danger_description !== cause.common_danger_description
          ? { localDescription: cause.danger_description } : {}),
        label: cause.danger_description || cause.danger_cause || `유해·위험요인 #${cause.danger_cause_id}`,
      }))),
  })));
}

export function planRegistrationCopy(kind, snapshot, { sourceId, itemIds, targetIds }) {
  const source = snapshot.find((row) => row.id === idOf(sourceId));
  const selectedItems = [...new Set(itemIds.map(idOf))];
  const selectedTargets = [...new Set(targetIds.map(idOf))];
  if (!source || !selectedItems.length || !selectedTargets.length) {
    throw new Error("원본, 복사할 항목, 대상을 모두 선택하세요.");
  }
  if (selectedItems.some((id) => !id || !source.items.some((item) => item.id === id))
    || selectedTargets.some((id) => !id || id === source.id || !snapshot.some((row) => row.id === id))) {
    throw new Error("선택한 원본 또는 대상이 변경되었습니다. 선택을 다시 확인하세요.");
  }
  const items = source.items.filter((item) => selectedItems.includes(item.id));
  const rows = selectedTargets.map((id) => {
    const target = snapshot.find((row) => row.id === id);
    const additions = items.filter((item) => !target.items.some((existing) => existing.id === item.id));
    return { target, items: additions, skipped: items.length - additions.length };
  });
  const payload = rows.filter((row) => row.items.length).map(({ target, items: additions }) => {
    if (kind === "machine") return {
      machineData: { machine_id: target.id },
      processData: additions.map((item) => ({ process_id: item.id })),
    };
    if (kind !== "cause") throw new Error("지원하지 않는 복사 유형입니다.");
    return {
      registerAssetData: { register_asset_id: target.id },
      situationData: additions.map((item) => ({ danger_cause_id: item.id,
        ...(item.localDescription ? { local_description: item.localDescription } : {}) })),
    };
  });
  return {
    source, items, rows, payload,
    added: rows.reduce((sum, row) => sum + row.items.length, 0),
    skipped: rows.reduce((sum, row) => sum + row.skipped, 0),
  };
}

export async function loadRegistrationCopy(api, kind) {
  const read = async (endpoint, params) => {
    const response = await api.get(endpoint, params);
    if (!Array.isArray(response.data)) throw new Error("복사할 데이터를 불러오지 못했습니다.");
    return response.data;
  };
  const assets = await read("/getRegisterAsset", { params: { filter: "registered" } });
  if (kind === "machine") {
    return buildCopySnapshot(kind, { assets, machines: await read("/getStandardMachine") });
  }
  return buildCopySnapshot(kind, {
    assets, causes: await read("/getRegisterCause", { params: { filter: "registered" } }),
  });
}
